import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { AlertCircle } from 'lucide-react';

interface PinUser {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string | null;
  position: string | null;
  app_role: 'employe' | 'contremaitre' | 'manager';
}

export default function LoginPin() {
  const [pin, setPin] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const { toast } = useToast();
  const PIN_LENGTH = 6;

  const onSubmit = async () => {
    setError(null);
    setLoading(true);
    
    try {
      // Avec RegExp(string), il faut "\\d" pour matcher un chiffre (pas "\\\\d")
      const pinRegex = new RegExp(`^\\d{${PIN_LENGTH}}$`);
      if (!pinRegex.test(pin)) {
        setError(`Entrez un PIN à ${PIN_LENGTH} chiffres`);
        return;
      }

      // Connexion via Supabase
      console.log('🔍 Tentative de connexion via Supabase...');
      const { data, error: rpcError } = await supabase.rpc('verify_employee_pin_only', { 
        p_pin: pin 
      });

      if (rpcError) throw rpcError;
      
      if (!data || data.length === 0) {
        setError('PIN invalide');
        return;
      }

      const user = data[0] as PinUser;
      
      // Stocker les informations de session
      localStorage.setItem('pin_user_id', user.id);
      localStorage.setItem('pin_user_name', `${user.first_name} ${user.last_name}`);
      localStorage.setItem('pin_user_role', user.app_role);
      localStorage.setItem('pin_user_email', user.email);
      localStorage.setItem('manager_pin', pin); // Pour les RPC sécurisées
      localStorage.setItem('auth_method', 'pin'); // Différencier de l'auth Supabase
      localStorage.setItem('pin_auth_source', 'supabase');
      
      // Marquer comme connecté
      localStorage.setItem('is_authenticated', 'true');
      window.dispatchEvent(new Event('pin-auth-changed'));

      toast({
        title: "Connexion réussie (Supabase)",
        description: `Bienvenue ${user.first_name} !`,
      });

      // Redirection vers la page d'accueil
      navigate('/');
      
    } catch (e) {
      console.error('Erreur de connexion PIN:', e);
      const msg = (e as { message?: string })?.message ?? "Erreur de connexion";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && pin.length === PIN_LENGTH) {
      onSubmit();
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-sm">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl font-bold">Connexion par PIN</CardTitle>
          <p className="text-sm text-muted-foreground">
            Entrez votre code PIN à {PIN_LENGTH} chiffres
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="pin">Code PIN</Label>
            <Input
              id="pin"
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={PIN_LENGTH}
              minLength={PIN_LENGTH}
              value={pin}
              onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
              onKeyPress={handleKeyPress}
              placeholder="••••••"
              className="text-center text-2xl font-mono tracking-widest"
              autoFocus
            />
          </div>
          
          {error && (
            <div className="flex items-center gap-2 p-3 text-sm text-destructive bg-destructive/10 rounded-md">
              <AlertCircle className="h-4 w-4" />
              {error}
            </div>
          )}
          
          <Button 
            className="w-full" 
            onClick={onSubmit} 
            disabled={loading || pin.length !== PIN_LENGTH}
            size="lg"
          >
            {loading ? "Connexion..." : "Se connecter"}
          </Button>
          
          <div className="text-center">
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => navigate('/auth')}
            >
              Connexion classique
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
